import React, { useState } from 'react';
import { Button, Modal, Typography, Result, App, Spin } from 'antd';
import { Add as AddIcon } from '@mui/icons-material';
import { CheckCircleFilled } from '@ant-design/icons';
import InfiniteScroll from 'react-infinite-scroll-component';
import StyledTable from '../Components/Tables/StyledTable';
import AddToCatalogStepper from './AddToCatalogStepper';
import { greyScaleTokens, lightBaseTokens } from '../../theme/theme';
import { configure3DViewer } from '../../services/utils/configure3DViewerService';
import { applyVariantFilters } from '../../services/data/ARSCatalogFiltersData';



const ProductTable = ({
    columns,
    filteredProducts,
    loading,
    loadingMore,
    loadingVariants,
    expandedRowKeys,
    handleTableChange, // This will be removed, but let's keep it for now to avoid breaking the parent
    variantColumns,
    variants,
    setExpandedRowKeys,
    selectedVariants,
    handleVariantSelection,
    columnWidths,
    // New props
    handleLoadMore,
    hasMore,
    // 🎯 NUOVO: Props per il filtro ibrido delle varianti
    activeFilters,
    // 🎯 NUOVO: Props per il loading granulare delle varianti
    loadingVariantsByProduct,
    loadProductVariants,
    // 🎯 NUOVO: Props per gestire varianti specifiche da ricerca e showNews
    matchedVariantsFromSearch,
    newVariantsFromShowNews,
    isSearchMode,
}) => {
    // Hook per accedere al message API
    const { message } = App.useApp();

    // State for modal visibility and selected items
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedCatalog, setSelectedCatalog] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);

    // State per la configurazione 3D viewer
    const [configuring3DViewer, setConfiguring3DViewer] = useState(false);

    // State per memorizzare i dati dell'ultima aggiunta per la configurazione 3D
    const [lastAddedData, setLastAddedData] = useState(null);

    // Ref for the IntersectionObserver target
    // const observerTarget = useRef(null); // Rimosso

    // IntersectionObserver logic
    // Rimossa la logica dell'IntersectionObserver


    // Function to handle opening the modal
    const handleAddToCatalog = (record) => {
        // Reset catalog selection
        setSelectedCatalog(null);
        setCurrentStep(0);

        // Reset variant selections for this product
        if (record && record.id && selectedVariants[record.id]) {
            const productId = record.id;
            const currentlySelected = selectedVariants[productId] || [];

            // Deselect each variant individually
            currentlySelected.forEach(variantId => {
                handleVariantSelection(productId, variantId, false);
            });
        }

        // Set the selected product and open the modal
        setSelectedProduct(record);
        setIsModalVisible(true);

        // Reset stepper to first step
        if (window.resetStepperToFirst) {
            window.resetStepperToFirst();
        }
    };

    // Function to handle closing the modal
    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedCatalog(null); // Reset catalog selection

        // Reset variant selections for the current product
        if (selectedProduct && selectedProduct.id) {
            handleVariantSelection(selectedProduct.id, [], false);
        }

        // Nota: lo stepper viene resettato al primo step nel suo componente
        // tramite l'effetto useEffect che si attiva quando il componente viene montato
    };

    // Function to handle closing the confirmation modal
    const handleCloseConfirmModal = async () => {
        // Se abbiamo dati dell'ultima aggiunta, configura il 3D viewer
        if (lastAddedData && !configuring3DViewer) {
            setConfiguring3DViewer(true);

            try {
                const { catalogId, catalogProductId, catalogVariantIds } = lastAddedData;

                const result = await configure3DViewer(catalogId, catalogProductId, catalogVariantIds);

                if (!result.success) {
                    message.error('Failed to configure 3D viewer');
                }
            } catch (error) {
                message.error('Failed to configure 3D viewer');
            } finally {
                setConfiguring3DViewer(false);
            }
        }

        setIsConfirmModalVisible(false);

        // Reset all selections
        setSelectedCatalog(null);
        setLastAddedData(null);

        // Reset variant selections for the current product
        if (selectedProduct && selectedProduct.id) {
            handleVariantSelection(selectedProduct.id, [], false);
        }

        // Reset selected product
        setSelectedProduct(null);
    };



    // Function to show success message after adding to catalog
    const handleShowSuccessModal = (addResultData = null) => {
        // Memorizza i dati per la configurazione del 3D viewer se disponibili
        if (addResultData && addResultData.catalogProductId && addResultData.catalogVariantIds && selectedCatalog) {
            setLastAddedData({
                catalogId: selectedCatalog,
                catalogProductId: addResultData.catalogProductId,
                originalProductId: selectedProduct?.id, // ID del prodotto originale
                catalogVariantIds: addResultData.catalogVariantIds,
                existingProduct: addResultData.existingProduct // Aggiunto flag per sapere se il prodotto esisteva già
            });
        }

        // Show the success modal
        setIsConfirmModalVisible(true);

        // Close the main modal
        setIsModalVisible(false);
    };



    // Reorganize columns in the specified order using centralized widths
    const reorderColumns = (originalColumns) => {
        const columnOrder = ['imgUrl', 'modelName', 'variantsCount', 'brandName', 'lastUpdate', 'addToCatalog'];
        const columnMap = {};

        // Create a map of existing columns
        originalColumns.forEach(col => {
            if (col.key === 'select') {
                columnMap['addToCatalog'] = {
                    title: 'Add To Catalog',
                    key: 'addToCatalog',
                    fixed: 'right',
                    width: columnWidths?.PRODUCT_SELECT || '5%',
                    align: 'center',
                    render: (_, record) => (
                        <Button
                            type="primary"
                            icon={<AddIcon />}
                            onClick={() => handleAddToCatalog(record)}
                            style={{
                                padding: '4px 8px',
                                height: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: 'auto'
                            }}
                        />
                    ),
                };
            } else {
                // Preserva TUTTE le proprietà originali e applica larghezze/allineamenti centralizzati
                const colWithCustomProps = { ...col };

                switch (col.key) {
                    case 'imgUrl':
                        colWithCustomProps.width = columnWidths?.PRODUCT_IMAGE || '10%';
                        colWithCustomProps.align = 'center';
                        break;
                    case 'modelName':
                        colWithCustomProps.width = columnWidths?.PRODUCT_NAME || '30%';
                        colWithCustomProps.align = 'left';
                        break;
                    case 'variantsCount':
                        colWithCustomProps.width = columnWidths?.PRODUCT_VARIANTS || '10%';
                        colWithCustomProps.align = 'right';
                        break;
                    case 'brandName':
                        colWithCustomProps.width = columnWidths?.PRODUCT_BRAND || '30%';
                        colWithCustomProps.align = 'left';
                        break;
                    case 'lastUpdate':
                        colWithCustomProps.width = columnWidths?.PRODUCT_LAST_UPDATE || '15%';
                        colWithCustomProps.align = 'left';
                        break;
                    default:
                        break;
                }

                columnMap[col.key] = colWithCustomProps;
            }
        });

        // Reorganize columns in the desired order
        return columnOrder.map(key => columnMap[key]).filter(Boolean);
    };

    const modifiedColumns = reorderColumns(columns);

    return (
        <>
            <InfiniteScroll
                dataLength={filteredProducts.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <Spin />
                    </div>
                }
                endMessage={
                    filteredProducts.length > 0 ? (
                        <p style={{ textAlign: 'center', color: greyScaleTokens.grey600, fontStyle: 'italic' }}>
                            All products have been loaded
                        </p>
                    ) : null
                }
                scrollableTarget="page-content-wrapper"
            >
                <StyledTable
                    columns={modifiedColumns}
                    dataSource={filteredProducts}
                    rowKey="id"
                    loading={loading && filteredProducts.length === 0} // Mostra loader solo al primo caricamento
                    pagination={null}
                    size="middle"
                    scroll={{ x: 1000 }}
                    containerStyle={{ boxShadow: 'none' }}
                    expandable={{
                        expandedRowRender: (record) => {
                            // Filtra la colonna Select dalle colonne delle varianti
                            const filteredVariantColumns = variantColumns.filter(column => column.key !== 'select' && column.dataIndex !== 'select');

                            // 🎯 NUOVO: Applica filtri sia di stato che di size/frameColor/showNews alle varianti
                            let productVariants = (variants[record.id] || [])
                                .filter(variant => variant.status === 'Published')
                                .map(variant => ({ ...variant, productId: record.id }));



                            // 🎯 IMPORTANTE: Se in modalità ricerca e abbiamo varianti specifiche,
                            // NON applicare filtri aggiuntivi perché le varianti sono già pre-filtrate
                            const hasSpecificVariants = isSearchMode && matchedVariantsFromSearch?.[record.id];
                            const hasNewsVariants = activeFilters?.showNews && newVariantsFromShowNews?.[record.id];

                            if (!hasSpecificVariants && !hasNewsVariants && activeFilters && (activeFilters.sizes?.length > 0 || activeFilters.frameColors?.length > 0 || activeFilters.showNews)) {
                                // Applica filtri solo se NON siamo in modalità ricerca con varianti specifiche
                                productVariants = applyVariantFilters(productVariants, {
                                    sizes: activeFilters.sizes || [],
                                    frameColors: activeFilters.frameColors || [],
                                    showNews: activeFilters.showNews || false
                                });
                            }

                            return (
                                <div style={{ padding: "clamp(0.5rem, 0.4rem + 0.5vw, 0.625rem)" }}>
                                    <Typography.Text strong style={{ display: 'block', marginBottom: 'clamp(0.5rem, 0.4rem + 0.5vw, 0.75rem)', fontSize: "clamp(0.875rem, 0.8rem + 0.25vw, 1rem)" }}>
                                        Available variants:
                                        {/* 🎯 NUOVO: Mostra il conteggio delle varianti filtrate o trovate */}
                                        <span style={{ fontWeight: 'normal', color: greyScaleTokens.grey600, marginLeft: '0.5rem' }}>
                                            ({productVariants.length}
                                            {isSearchMode && matchedVariantsFromSearch?.[record.id]
                                                ? ' matching search'
                                                : activeFilters?.showNews && newVariantsFromShowNews?.[record.id]
                                                    ? ' new variants'
                                                    : activeFilters && (activeFilters.sizes?.length > 0 || activeFilters.frameColors?.length > 0)
                                                        ? ' matching filters'
                                                        : ' found'
                                            })
                                        </span>
                                    </Typography.Text>
                                    <StyledTable
                                        columns={filteredVariantColumns}
                                        dataSource={productVariants}
                                        rowKey="id"
                                        loading={loadingVariantsByProduct?.[record.id] || (!variants[record.id] && loadingVariants)} // 🎯 NUOVO: Loading granulare per prodotto
                                        pagination={null} // FIX: Use null to completely disable pagination
                                        size="small"
                                        scroll={{ x: 800 }}
                                        containerStyle={{ boxShadow: 'none', border: 'none' }}
                                        locale={{
                                            emptyText: loadingVariantsByProduct?.[record.id] ?
                                                <Spin size="small" /> :
                                                !variants[record.id] ?
                                                    'Loading variants...' :
                                                    productVariants.length === 0 ?
                                                        'No variants match the current filters' :
                                                        'No variants available'
                                        }}
                                    />
                                </div>
                            );
                        },
                        expandedRowKeys,
                        onExpand: (expanded, record) => {
                            if (expanded) {
                                setExpandedRowKeys(prev => [...prev, record.id]);
                                // 🎯 NUOVO: Carica le varianti quando si espande il prodotto
                                if (loadProductVariants) {
                                    loadProductVariants(record.id, record);
                                }
                            } else {
                                setExpandedRowKeys(prev => prev.filter(key => key !== record.id));
                            }
                        }
                    }}
                />
            </InfiniteScroll>

            {/* Add To Catalog Modal with Stepper */}
            <Modal
                title={
                    <Typography.Title level={4} style={{ marginBottom: '3rem' }}>
                        {selectedProduct ? `Add To Catalogue - ${selectedProduct.modelName} - ${selectedProduct.brandName}` : 'Add To Catalogue'}
                    </Typography.Title>
                }
                open={isModalVisible}
                onCancel={handleCloseModal}
                width="75%"
                centered={true}
                style={{
                    margin: "1rem",
                    marginLeft: "calc(80px + 1rem)"
                }}
                footer={null}
            >
                {selectedProduct && (
                    <AddToCatalogStepper
                        selectedProduct={selectedProduct}
                        variants={variants}
                        selectedVariants={selectedVariants}
                        handleVariantSelection={handleVariantSelection}
                        selectedCatalog={selectedCatalog}
                        setSelectedCatalog={setSelectedCatalog}
                        onCancel={handleCloseModal}
                        onFinish={handleShowSuccessModal}
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                    />
                )}
            </Modal>

            {/* Confirmation Modal */}
            <Modal
                open={isConfirmModalVisible}
                onCancel={configuring3DViewer ? null : handleCloseConfirmModal}
                centered={true}
                closable={!configuring3DViewer}
                maskClosable={!configuring3DViewer}
                footer={[
                    <Button
                        key="close"
                        type="primary"
                        onClick={handleCloseConfirmModal}
                        loading={configuring3DViewer}
                        disabled={configuring3DViewer}
                    >
                        {configuring3DViewer ? 'Configuring 3D Viewer...' : 'Close'}
                    </Button>
                ]}
            >
                <Result
                    icon={<CheckCircleFilled style={{ color: lightBaseTokens.colorSuccess, fontSize: '72px' }} />}
                    title={lastAddedData?.existingProduct ? "Variants Added to Catalogue" : "Product Added to Catalogue"}
                    subTitle={
                        configuring3DViewer
                            ? (lastAddedData?.existingProduct
                                ? "The variants have been added to the existing product in the catalogue. Configuring 3D viewer..."
                                : "The product and variants have been added to the catalogue. Configuring 3D viewer...")
                            : (lastAddedData?.existingProduct
                                ? `The variants have been added to the existing product in the catalogue. Go to "My Catalogue" to view the Asset List`
                                : `The product has been successfully added to the catalogue. Go to "My Catalogue" to view the Asset List`)
                    }
                />
            </Modal>
        </>
    );
};

export default ProductTable;